import { Box, BoxProps, Button, Flex, Text } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { OmnisearchOccurrencePoster } from '@/components/ui/features/lecture/LectureOmnisearch/components/OmnisearchOccurrencePoster';
import { OmnisearchOccurrenceVideo } from '@/components/ui/features/lecture/LectureOmnisearch/components/OmnisearchOccurrenceVideo';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { FoundIn, OmnisearchOccurrence } from '@/resources/OmnisearchOccurrence';

interface IOmnisearchVideoPostersProps extends BoxProps {
  data: Array<OmnisearchOccurrence>;
  autocorrectedQuery: string;
}

enum SearchScope {
  ALL = 'all',
  VIDEO = 'video',
  POSTER = 'poster',
}

export const OmnisearchVideoPosters: FC<IOmnisearchVideoPostersProps> = ({ data, autocorrectedQuery }) => {
  const { t } = useTranslation();
  const { searchScope = SearchScope.ALL, change, query } = useQueryParams<{ query: string; searchScope: string }>({
    query: StringParam,
    searchScope: StringParam,
  });

  const posterOccurrences = data?.find((d) => d.foundIn === FoundIn.POSTER_DOCUMENT);
  const videoOccurrences = data?.find((d) => d.foundIn === FoundIn.TRANSCRIPT_EN);

  const scopeOccurrenceCountMapper = {
    [SearchScope.POSTER]: posterOccurrences?.details?.length ?? 0,
    [SearchScope.VIDEO]: videoOccurrences?.details?.length ?? 0,
    [SearchScope.ALL]: (posterOccurrences?.details?.length ?? 0) + (videoOccurrences?.details?.length ?? 0),
  };

  const hasOccurrences = scopeOccurrenceCountMapper[searchScope] !== 0;

  function setSearchScope(ss: SearchScope) {
    change({ query, searchScope: ss }, { method: 'replace', shallow: true });
  }

  return (
    <Box>
      <Box borderBottom="1px solid gray" borderBottomColor="gray.200" px="4">
        <Flex maxW="300px" justifyContent="space-between" my="4" alignItems="center">
          <Text color="black">search:</Text>
          {Object.values(SearchScope).map((ss) => (
            <Button
              key={ss}
              isDisabled={scopeOccurrenceCountMapper[ss] === 0}
              onClick={() => setSearchScope(ss)}
              bg={ss === searchScope ? 'violet.500' : 'auto'}
              color={ss === searchScope ? 'white' : 'black'}
              variant="outline"
              size="sm"
            >
              {ss}
            </Button>
          ))}
        </Flex>
      </Box>

      <Box px="4" py="2" borderBottom="1px solid gray" borderBottomColor="gray.200">
        <Flex mb="2" justifyContent="center">
          {!hasOccurrences && (
            <Text mt="2" fontFamily="heading" color="black">
              {t('lectureOmnisearch.noResultsInfoMessage', { query: autocorrectedQuery || query })}
            </Text>
          )}
          {hasOccurrences && (
            <Text fontSize="sm" color="gray.700">
              {t('lectureOmnisearch.showingXResults', { count: scopeOccurrenceCountMapper[searchScope] })}
            </Text>
          )}
        </Flex>
        <Box h="full" maxH={{ base: '812px', md: '460px', lg: '424px' }} overflowY="scroll">
          {searchScope !== SearchScope.POSTER &&
            videoOccurrences?.details.map((po, i) => (
              <OmnisearchOccurrenceVideo key={`video-${i}`} mb="4" context={po} occurrenceIndex={i} />
            ))}
          {searchScope !== SearchScope.VIDEO &&
            posterOccurrences?.details.map((po, i) => (
              <OmnisearchOccurrencePoster key={`poster-${i}`} mb="4" context={po} occurrenceIndex={i} />
            ))}
        </Box>
      </Box>
    </Box>
  );
};
